/**
 * **shader / filter 的追踪点。**
 *
 * 动机：`[.WebGL-…]RENDER WARNING: there is no texture bound to the unit N`
 * 这类报错**只说 unit 号，不说哪条 shader** —— 而我们的滤镜是嵌套的（背景场 +
 * 半调 + 故障 + 淡出…），unit 号根本对不上任何一个名字。所以每个自定义
 * `GlProgram` 在编译时都做两件事：
 *
 * 1. 在片元源码最前面塞一个 `#define S3_SHADER_<NAME> 1` 别名 ——
 *    WebGL 调试器（Spector.js / `WEBGL_debug_shaders`）展出源码时一眼能认出是谁；
 * 2. 把「名字 → 它声明了哪些 sampler2D」记进一张表，dev 下挂到
 *    `globalThis.__S3_SHADERS__`，控制台敲一下就能列出全部。
 *
 * 这样下次再出 unit N 的警告，配合这张表就能反查是哪个滤镜少绑了贴图。
 */
export interface ShaderTrace {
  readonly name: string;
  readonly samplers: readonly string[];
}

const registry = new Map<string, ShaderTrace>();
let installed = false;

/** 从片元源码里抓出所有 `uniform sampler2D <name>;` */
export function samplerNames(fragment: string): string[] {
  const out: string[] = [];
  const re = /uniform\s+sampler2D\s+(\w+)\s*;/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(fragment)) !== null) out.push(m[1] ?? '');
  return out;
}

function installGlobal(): void {
  if (installed) return;
  installed = true;
  if (!import.meta.env.DEV) return;
  (globalThis as unknown as { __S3_SHADERS__?: () => ShaderTrace[] }).__S3_SHADERS__ = () => shaderTrace();
}

/** 登记一个已编译的自定义 shader（由 `quadProgram` 调）返回它的别名 */
export function registerShader(name: string, fragment: string): string {
  registry.set(name, { name, samplers: samplerNames(fragment) });
  installGlobal();
  return aliasOf(name);
}

/** shader 名 → 合法的 GLSL 标识符别名 */
export function aliasOf(name: string): string {
  return 'S3_SHADER_' + name.replace(/[^A-Za-z0-9]/g, '_').toUpperCase();
}

/** 现在登记了哪些 shader、各自声明了哪些 sampler */
export function shaderTrace(): ShaderTrace[] {
  return [...registry.values()];
}

/**
 * **dev 下的贴图存活检查。**
 *
 * 绑定一张已销毁 / 为空的 `TextureSource` 是「unit N 没有贴图」最常见的成因，
 * 而 Pixi 对这种情况**不报错**（静默跳过），只在驱动层留一条 unit 警告。
 * 所以每次 `setMask` / `setCrack` 都问一句：还活着吗？
 */
export function warnIfDeadSource(label: string, source: unknown): void {
  if (!import.meta.env.DEV) return;
  const dead =
    source === null ||
    source === undefined ||
    (source as { destroyed?: boolean }).destroyed === true;
  if (dead) {
    console.warn('[render] ' + label + ' 绑定了一张已销毁 / 为空的贴图（unit 警告的真凶？）', source);
  }
}
